import {
  Box,
  Flex,
  Stack,
  Text,
  Title,
  Paper,
  Table,
  Badge,
  Button,
  ActionIcon,
  Group,
  ScrollArea,
  UnstyledButton,
  Collapse,
  Tabs,
  Tooltip,
} from '@mantine/core';
import {
  IconTarget,
  IconChartBar,
  IconReportAnalytics,
  IconSettings,
  IconArrowLeft,
  IconNotes,
  IconArrowRight,
} from '@tabler/icons-react';
import {
  FunnelRow,
  GoalRow,
  ALL_METRICS,
  LifecycleData,
  LifecycleRow,
  NetworkRow,
  Tone,
  toneColor,
} from './types';
import {
  SectionHeader,
  GoalBreakdownTypeSelect,
  GoalBreakdownTable,
  GoalVariationBreakdown,
  MetricCellTd,
  MetricSource,
  metricDisplayLabel,
  AttributionHelp,
  SectionHelp,
} from './shared';
import { FunnelTrafficBreakdown } from './FunnelTrafficBreakdown';
import { AggregatedLifecycleBody } from './AggregatedLifecycleBody';
import { AdvancedFilterTables, AdvancedFilterTablesData } from './AdvancedFilterTables';

type FunnelTabProps = {
  funnelRows: FunnelRow[];
  goalRows: GoalRow[];
  // Passed straight through to GoalBreakdownTable; this tab owns no Customer Type state.
  goalCustomerType?: any;
  drillPrimarySummary?: MetricSource | null;
  drillLifecycle?: LifecycleData | null;
  drillLoading?: boolean;
  isDarkTheme?: boolean;
  lifecycleRows: LifecycleRow[];
  lifecycleGroupBy: string;
  setLifecycleGroupBy: (v: string) => void;
  trafficBaseParams: Record<string, any>;
  trafficBreakdownType?: 'none' | 'device' | 'hour' | 'cust_type';
  trafficVelocityContext?: any;
  onKeywordExport?: () => Promise<string>;
  hiddenCampaignKeys?: string[];
  onToggleHiddenCampaign?: (key: string) => void;
  trafficMetrics: string[];
  trafficFunnelMetricsByGoal: Record<string, string[]>;
  trafficGoalsEnabled: Record<string, boolean>;
  onFunnelTrafficGoalsChange?: (goals: any[]) => void;
  selectedFunnelId: string | null;
  setSelectedFunnelId: (id: string | null) => void;
  // Campaign notes live on the campaign, so the Funnel view is the only place they are offered.
  onCampaignNotesOpen?: (campaignId: string, campaignName: string) => void;
  openSettings: (context: string, scope: string) => void;
  funnelAdditionalMetrics: string[];
  // Drives the attribution help note above the campaign list.
  multiCampaign?: boolean;
  attribution?: string;
  testVariationActive?: boolean;
  testVariationMessage?: string;
  onVariationOpen?: (goal: any, variation: any) => void;
  onVariationJourneyOpen?: (goal: any, variation: any) => void;
  onVariationConversionLogOpen?: (goal: any, variation: any) => void;
  advancedFilterTables?: AdvancedFilterTablesData;
  goalBreakdownOpened: boolean;
  goalBreakdownType: 'none' | 'device' | 'hour';
  onGoalBreakdownTypeChange: (v: 'none' | 'device' | 'hour') => void;
  setGoalBreakdownOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  lifecycleOpened: boolean;
  setLifecycleOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  trafficOpened: boolean;
  setTrafficOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  // Opens the Customer Journey drawer for a goal row (Conversions cell click).
  onGoalJourneyOpen?: (goalKey: string, goalLabel: string) => void;
  onGoalConversionLogOpen?: (goalKey: string, goalLabel: string, options?: { initialKind: 'undefined' }) => void;
  // Opens the Customer Journey drawer for a source row in the Source advanced-filter table.
  onSourceJourneyOpen?: (args: { sourceId: string; sourceName: string; goalKey: string; goalLabel: string }) => void;
  // Opens the Customer Journey drawer for a row in the Tracking Tag advanced-filter table.
  onTrackingJourneyOpen?: (args: { trackingTagData: string; entityLabel: string; goalKey: string; goalLabel: string }) => void;
  // Opens the Customer Journey drawer for a row in a Custom Tag advanced-filter sub-table.
  onCustomJourneyOpen?: (args: { tagId: string; tagValue: string; tagName: string; goalKey: string; goalLabel: string }) => void;
  // Opens the Customer Journey drawer for an ad-network row in Traffic Breakdown.
  onTrafficJourneyOpen?: (args: {
    row: NetworkRow;
    goalKey: string;
    goalLabel: string;
    level: 'campaign' | 'adset' | 'ad';
    parent_campaign_id: string;
    parent_adset_id: string;
  }) => void;
  // Opens the Velocity drawer for a goal from the Lifecycle clock icon.
  onLifecycleVelocityOpen?: (args: {
    goalKey: string;
    goalLabel: string;
    cohortKey: string;
    cohortKind: 'daily' | 'range' | 'weekday' | 'weekend' | 'summary';
    cohortStart?: string;
    cohortEnd?: string;
  }) => void;
};

export function FunnelTab({
  funnelRows,
  goalRows,
  goalCustomerType,
  drillPrimarySummary = null,
  drillLifecycle = null,
  drillLoading = false,
  isDarkTheme = false,
  lifecycleRows,
  lifecycleGroupBy,
  setLifecycleGroupBy,
  trafficBaseParams,
  trafficBreakdownType = 'none',
  trafficVelocityContext,
  onKeywordExport,
  hiddenCampaignKeys,
  onToggleHiddenCampaign,
  trafficMetrics,
  trafficFunnelMetricsByGoal,
  trafficGoalsEnabled,
  onFunnelTrafficGoalsChange,
  selectedFunnelId,
  setSelectedFunnelId,
  onCampaignNotesOpen,
  openSettings,
  funnelAdditionalMetrics,
  multiCampaign = false,
  attribution = 'first_click',
  testVariationActive = false,
  testVariationMessage = '',
  onVariationOpen,
  onVariationJourneyOpen,
  onVariationConversionLogOpen,
  advancedFilterTables,
  goalBreakdownOpened: funnelGoalBreakdownOpened,
  goalBreakdownType,
  onGoalBreakdownTypeChange,
  setGoalBreakdownOpened: setFunnelGoalBreakdownOpened,
  lifecycleOpened: funnelLifecycleOpened,
  setLifecycleOpened: setFunnelLifecycleOpened,
  trafficOpened: funnelTrafficOpened,
  setTrafficOpened: setFunnelTrafficOpened,
  onGoalJourneyOpen,
  onGoalConversionLogOpen,
  onSourceJourneyOpen,
  onTrackingJourneyOpen,
  onCustomJourneyOpen,
  onTrafficJourneyOpen,
  onLifecycleVelocityOpen,
}: FunnelTabProps) {
  const funnelMetrics = funnelAdditionalMetrics;
  // Only campaigns that actually ran a test get the variation view.
  const hasGoalVariations = goalRows.some(
    (g) => Array.isArray((g as any).variations) && (g as any).variations.length > 0
  );
  // Same order as Additional Metrics lists them, minus the campaign-level columns.
  const goalBreakdownHidden = new Set(['spend', 'aov', 'profit', 'roi', 'uniques', 'visits', 'ctr', 'cpm']);
  const goalBreakdownOrder = ALL_METRICS.filter((m) => !goalBreakdownHidden.has(m.toLowerCase()));
  const goalBreakdownMetrics = goalBreakdownOrder.filter((m) =>
    funnelAdditionalMetrics.some((sel) => sel.toLowerCase() === m.toLowerCase())
      && !goalBreakdownHidden.has(m.toLowerCase())
  );

  const selectedFunnel = funnelRows.find((f) => f.id === selectedFunnelId) || null;

  // Conversions here count test entrants only when this is on, so the state has to be visible.
  const testVariationBadge = (
    <Tooltip
      withArrow
      multiline
      width={280}
      label={
        testVariationActive
          ? 'Counts only visitors who entered an A/B test, so these numbers may be lower than the Total tab.'
          : 'Counts every visitor, matching the Total tab.'
      }
    >
      <Badge
        variant={testVariationActive ? 'filled' : 'outline'}
        color={testVariationActive ? 'teal' : 'gray'}
        size="sm"
        sx={{ cursor: 'help' }}
      >
        {testVariationActive ? 'Test Variations: On' : 'Test Variations: Off'}
      </Badge>
    </Tooltip>
  );

  if (!selectedFunnel) {
    return (
      <Tabs.Panel value="funnel" pt="lg">
        <Box>
          <Flex justify="space-between" align="center" gap="sm" mb="xs">
            <Flex align="center" gap="xs">
              <SectionHelp section="summary" title="Funnel metrics" />
              {/* Only ambiguous with several campaigns - that is when the rows stop summing to the Total. */}
              {multiCampaign && (
                <>
                  <AttributionHelp multiCampaign attribution={attribution} />
                  <Text size="sm" color="dimmed">
                    Each conversion is credited to one campaign, so these rows may not add up to the Total.
                  </Text>
                </>
              )}
            </Flex>
            <Group spacing="xs">
              {testVariationBadge}
              <ActionIcon
                variant="default"
                size="md"
                title="Funnel Settings"
                onClick={() =>
                  openSettings(
                    'Funnel Settings',
                    'Applies to Funnel · Goal Breakdown'
                  )
                }
              >
                <IconSettings size="1rem" />
              </ActionIcon>
            </Group>
          </Flex>

          <Paper
            withBorder
            radius="md"
            p="sm"
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            })}
          >
            <ScrollArea type="auto" offsetScrollbars>
              <Table
                verticalSpacing="md"
                horizontalSpacing="md"
                sx={{
                  minWidth: (1 + funnelMetrics.length) * 110,
                  '& th, & td': { whiteSpace: 'nowrap' },
                }}
              >
                <thead>
                  <tr>
                    <th>
                      <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                        Visiopt Campaign
                      </Text>
                    </th>
                    {funnelMetrics.map((m) => (
                      <th key={m}>
                        <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                          {m}
                        </Text>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {funnelRows.length === 0 ? (
                    <tr>
                      <td colSpan={1 + funnelMetrics.length} style={{ whiteSpace: 'normal' }}>
                        <Text size="sm" color="dimmed" align="center" py="md">
                          Select a Visiopt campaign in filters to load funnels.
                        </Text>
                      </td>
                    </tr>
                  ) : (
                    funnelRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => setSelectedFunnelId(row.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <Flex align="center" gap={6}>
                            <Text color="teal" weight={600}>
                              {row.name}
                            </Text>
                            {onCampaignNotesOpen && (
                              <Tooltip
                                withArrow
                                withinPortal
                                label={Number(row.has_note) === 1
                                  ? `View or edit the note on ${row.name}`
                                  : `Add a note to ${row.name}`}
                              >
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  color={Number(row.has_note) === 1 ? 'teal' : 'gray'}
                                  aria-label="Campaign notes"
                                  onClick={(e) => {
                                    // The row itself drills into the funnel, so the icon must not.
                                    e.stopPropagation();
                                    onCampaignNotesOpen(row.id, row.name);
                                  }}
                                >
                                  <IconNotes size="0.85rem" />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Flex>
                        </td>
                        {funnelMetrics.map((m) => (
                          <MetricCellTd key={m} row={row} metric={m} />
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          </Paper>
          <Flex justify="flex-end" mt="xs">
            <Text size="xs" color="dimmed">
              Click a row to open the funnel drilldown
            </Text>
          </Flex>
        </Box>
      </Tabs.Panel>
    );
  }

  type FunnelStep = {
    label: string;
    value: number;
    totalVisits?: number;
    ucr?: string;
    ucrNum?: number;
    cr?: string;
    prevStepPct?: string;
    prevStepNum?: number;
    dropPct?: number;
  };
  const formatPct = (n: number): string => (Number.isFinite(n) ? n.toFixed(2) : '0.00');

  // Parses "$1,234" / "12%" / "1,234" → 1234.
  const parseStat = (v: string | number | undefined): number => {
    if (typeof v === 'number') { return v; }
    if (!v) { return 0; }
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  };
  const drillRows: GoalRow[] = goalRows;
  const lpUniques = drillRows.length > 0
    ? Math.max(...drillRows.map((g) => parseStat((g as any).uniques)))
    : 0;
  const lpVisits = drillRows.length > 0
    ? Math.max(...drillRows.map((g) => parseStat((g as any).visits)))
    : 0;
  const funnelSteps: FunnelStep[] = drillRows.length > 0
    ? [
        { label: 'Landing Page', value: lpUniques, totalVisits: lpVisits },
        ...drillRows.map((g, i) => {
          const conv = g.conversions || 0;
          const prev = i === 0 ? lpUniques : drillRows[i - 1].conversions || 0;
          const prevStepNum = prev > 0 ? (conv / prev) * 100 : 0;
          const ucrNum = lpUniques > 0 ? (conv / lpUniques) * 100 : 0;
          return {
            label: g.goal,
            value: conv,
            ucr: g.ucr ?? formatPct(ucrNum),
            ucrNum,
            cr: g.cr ?? formatPct(lpVisits > 0 ? (conv / lpVisits) * 100 : 0),
            prevStepPct: formatPct(prevStepNum),
            prevStepNum,
            dropPct: prev > 0 ? Number((prevStepNum - 100).toFixed(2)) : 0,
          };
        }),
      ]
    : [];
  const convTone = (pct: number): Tone =>
    pct >= 60 ? 'good' : pct >= 50 ? 'warning' : 'poor';
  // Spend / ROAS come from the campaign's existing funnel-row metrics; conversions is the
  // selected campaign's primary-goal conversions (get_aggregated_funnel_rows).
  const stats = [
    { label: `${selectedFunnel.spend} spend`, color: '#3B82F6', bg: 'rgba(59,130,246,0.12)' },
    { label: `${selectedFunnel.roas} ROAS`, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
    { label: `${selectedFunnel.conversions} conv.`, color: '#F59E0B', bg: 'rgba(245,158,11,0.12)' },
  ];

  return (
    <Tabs.Panel value="funnel" pt="lg">
      <Stack spacing="md">
        <Flex align="center" gap="sm">
          <Button
            variant="subtle"
            size="xs"
            leftIcon={<IconArrowLeft size="1rem" />}
            onClick={() => setSelectedFunnelId(null)}
          >
            Back to funnels
          </Button>
          <Title order={4}>{selectedFunnel.name}</Title>
          {onCampaignNotesOpen && (
            <Tooltip
              withArrow
              withinPortal
              label={Number(selectedFunnel.has_note) === 1
                ? `View or edit the note on ${selectedFunnel.name}`
                : `Add a note to ${selectedFunnel.name}`}
            >
              <Button
                variant="subtle"
                size="xs"
                leftIcon={<IconNotes size="0.9rem" />}
                onClick={() => onCampaignNotesOpen(selectedFunnel.id, selectedFunnel.name)}
              >
                Notes
              </Button>
            </Tooltip>
          )}
          {testVariationBadge}
          <Group spacing="xs">
            {stats.map((item) => (
              <UnstyledButton
                key={item.label}
                sx={{
                  border: `1px solid ${item.color}`,
                  borderRadius: 8,
                  padding: '6px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  color: item.color,
                  backgroundColor: item.bg,
                  boxShadow: `0 0 6px ${item.bg}`,
                  lineHeight: 1.2,
                }}
              >
                {item.label}
              </UnstyledButton>
            ))}
          </Group>
        </Flex>

        <Paper
          withBorder
          radius="md"
          p="md"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          })}
        >
          <Text
            size="xs"
            color="dimmed"
            transform="uppercase"
            weight={700}
            mb="md"
            sx={{ letterSpacing: 0.5 }}
          >
            Funnel Flow
          </Text>

          <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
          <Flex align="center" gap={0} wrap="nowrap" sx={{ minWidth: 'max-content' }}>
            {funnelSteps.map((step, i) => {
              const isFirst = i === 0;
              const isLast = i === funnelSteps.length - 1;

              return (
                <Flex key={step.label} align="center" gap={0}>
                  {!isFirst && step.dropPct !== undefined && (
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      gap={2}
                      px="sm"
                      sx={{ minWidth: 52 }}
                    >
                      {/* Red only for a real loss; no change (or a gain) reads neutral. */}
                      <Text
                        size="xs"
                        weight={700}
                        color={step.dropPct < 0 ? undefined : 'dimmed'}
                        sx={step.dropPct < 0 ? { color: '#ef4444' } : undefined}
                      >
                        {step.dropPct.toFixed(2)}%
                      </Text>
                      <IconArrowRight size="0.85rem" opacity={0.45} />
                    </Flex>
                  )}

                  <Paper
                    withBorder
                    radius="md"
                    sx={(theme) => ({
                      width: 220,
                      height: 200,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      backgroundColor:
                        theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
                      borderColor:
                        theme.colorScheme === 'dark'
                          ? theme.colors.dark[4]
                          : theme.colors.gray[3],
                    })}
                  >
                    <Box
                      sx={(theme) => ({
                        height: 3,
                        flexShrink: 0,
                        backgroundColor: isFirst
                          ? theme.colors.teal[5]
                          : isLast
                          ? theme.colors.orange[5]
                          : theme.colorScheme === 'dark'
                          ? theme.colors.dark[3]
                          : theme.colors.gray[3],
                      })}
                    />

                    <Stack
                      spacing={4}
                      align="center"
                      justify="center"
                      px="md"
                      py="sm"
                      sx={{ flex: 1 }}
                    >
                      <Text
                        size="xs"
                        color="dimmed"
                        transform="uppercase"
                        weight={700}
                        sx={{ letterSpacing: 0.6 }}
                      >
                        {step.label}
                      </Text>
                      <Text weight={700} sx={{ fontSize: '1.75rem', lineHeight: 1.1 }}>
                        {step.value.toLocaleString()}
                      </Text>

                      {isFirst ? (
                        <>
                          <Text size="xs" color="dimmed">
                            Unique Visits
                          </Text>
                          {step.totalVisits !== undefined && (
                            <>
                              <Text
                                weight={700}
                                sx={{ fontSize: '1.25rem', lineHeight: 1.1, marginTop: 6 }}
                              >
                                {step.totalVisits.toLocaleString()}
                              </Text>
                              <Text size="xs" color="dimmed">
                                Total Visits
                              </Text>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <Text size="xs" color="dimmed">
                            Conversions
                          </Text>
                          {step.ucr !== undefined && (
                            <Text size="sm" weight={700}>
                              <Text
                                component="span"
                                weight={700}
                                sx={{ color: toneColor(convTone(step.ucrNum ?? 0)) }}
                              >
                                {String(step.ucr).replace('%', '')}%
                              </Text>{' '}
                              <Text component="span" size="xs" color="dimmed" weight={600}>
                                UCR
                              </Text>
                            </Text>
                          )}
                          {step.cr !== undefined && (
                            <Text size="xs" color="dimmed" weight={500}>
                              {String(step.cr).replace('%', '')}% CR
                            </Text>
                          )}
                          {step.prevStepPct !== undefined && (
                            <Text
                              size="xs"
                              weight={700}
                              sx={(theme) => ({
                                marginTop: 4,
                                color:
                                  theme.colorScheme === 'dark'
                                    ? theme.colors.teal[3]
                                    : theme.colors.teal[7],
                              })}
                            >
                              {step.prevStepPct}%{' '}
                              <Text component="span" size="xs" color="dimmed" weight={500}>
                                of previous step
                              </Text>
                            </Text>
                          )}
                        </>
                      )}
                    </Stack>
                  </Paper>
                </Flex>
              );
            })}
          </Flex>
          </Box>
        </Paper>

        <Paper
          withBorder
          radius="md"
          p="sm"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
          })}
        >
          <SectionHelp section="summary" title="Funnel metrics" />
          {/* Scrolls like the Total strip, so the last metrics stay reachable on a narrow screen. */}
          <ScrollArea type="auto" offsetScrollbars>
          <Table
            verticalSpacing="md"
            horizontalSpacing="md"
            sx={{
              minWidth: funnelMetrics.length * 110,
              '& th, & td': { whiteSpace: 'nowrap' },
            }}
          >
            <thead>
              <tr>
                {funnelMetrics.map((m) => (
                  <th key={m}>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                      {metricDisplayLabel(m)}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {funnelMetrics.map((m) => (
                  <MetricCellTd
                    key={m}
                    row={drillPrimarySummary ?? selectedFunnel}
                    metric={m}
                    size="md"
                  />
                ))}
              </tr>
            </tbody>
          </Table>
          </ScrollArea>
        </Paper>

        <Box>
          <SectionHeader
            icon={<IconTarget size="1rem" color="#ef4444" />}
            label="Goal Breakdown"
            info={<SectionHelp section="goal" title="Goal Breakdown" breakdownType={goalBreakdownType} />}
            actionNode={<GoalBreakdownTypeSelect value={goalBreakdownType} onChange={onGoalBreakdownTypeChange} />}
            opened={funnelGoalBreakdownOpened}
            toggle={() => setFunnelGoalBreakdownOpened((o) => !o)}
          />
          <Collapse in={funnelGoalBreakdownOpened}>
            <Paper
              withBorder
              radius="md"
              p="sm"
              mt="sm"
              sx={(theme) => ({
                backgroundColor:
                  theme.colorScheme === 'dark'
                    ? theme.colors.dark[6]
                    : theme.colors.gray[0],
              })}
            >
              <Text size="xs" color="dimmed" mb="sm">
                Click a conversion count to open its log, or the route icon to see customer journeys.
              </Text>
              {testVariationActive && testVariationMessage !== '' && (
                <Text size="xs" color="dimmed" mb="sm">
                  {testVariationMessage}
                </Text>
              )}
              {testVariationActive && hasGoalVariations ? (
                <GoalVariationBreakdown
                  customerType={goalCustomerType}
                  goalRows={goalRows}
                  metrics={goalBreakdownMetrics}
                  onVariationOpen={onVariationOpen}
                  onVariationJourneyOpen={onVariationJourneyOpen}
                  onVariationConversionLogOpen={onVariationConversionLogOpen}
                  onConversionLogOpen={
                    onGoalConversionLogOpen
                      ? (row, kind) => onGoalConversionLogOpen(
                        row.goal_key ?? '',
                        row.goal,
                        kind === 'undefined' ? { initialKind: 'undefined' } : undefined
                      )
                      : undefined
                  }
                />
              ) : (
                <GoalBreakdownTable
                  customerType={goalCustomerType}
                  goalRows={goalRows}
                  metrics={goalBreakdownMetrics}
                  onJourneyOpen={
                    onGoalJourneyOpen
                      ? (row) => onGoalJourneyOpen(row.goal_key ?? '', row.goal)
                      : undefined
                  }
                  onConversionLogOpen={
                    onGoalConversionLogOpen
                      ? (row, kind) => onGoalConversionLogOpen(
                      row.goal_key ?? '',
                      row.goal,
                      kind === 'undefined' ? { initialKind: 'undefined' } : undefined
                    )
                      : undefined
                  }
                />
              )}
            </Paper>
          </Collapse>
        </Box>

        {advancedFilterTables ? (
          <AdvancedFilterTables
            data={advancedFilterTables}
            metrics={funnelMetrics}
            isDarkTheme={isDarkTheme}
            onSourceJourneyOpen={onSourceJourneyOpen}
            onTrackingJourneyOpen={onTrackingJourneyOpen}
            onCustomJourneyOpen={onCustomJourneyOpen}
          />
        ) : null}

        <Box>
          <SectionHeader
            icon={<IconChartBar size="1rem" color="#3b82f6" />}
            label="Lifecycle"
            info={<SectionHelp section="lifecycle" title="Lifecycle" />}
            rightNode={
              <Badge
                color="blue"
                variant="outline"
                size="sm"
                sx={{ textTransform: 'none' }}
              >
                Cohort View
              </Badge>
            }
            opened={funnelLifecycleOpened}
            toggle={() => setFunnelLifecycleOpened((o) => !o)}
          />
          <Collapse in={funnelLifecycleOpened}>
            <AggregatedLifecycleBody
              lifecycleData={drillLifecycle ?? null}
              loading={drillLoading}
              isDarkTheme={isDarkTheme}
              onVelocityOpen={onLifecycleVelocityOpen}
            />
          </Collapse>
        </Box>

        <Box>
          <SectionHeader
            icon={<IconReportAnalytics size="1rem" color="#22c55e" />}
            label="Traffic Breakdown"
            info={<SectionHelp section="ad" title="Traffic Breakdown" breakdownType={trafficBreakdownType} />}
            opened={funnelTrafficOpened}
            toggle={() => setFunnelTrafficOpened((o) => !o)}
          />
          <Collapse in={funnelTrafficOpened}>
            <FunnelTrafficBreakdown
              enabled={funnelTrafficOpened && !!selectedFunnelId}
              funnelId={selectedFunnelId}
              baseParams={trafficBaseParams}
              breakdownType={trafficBreakdownType}
              trafficMetrics={trafficMetrics}
              trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
              trafficGoalsEnabled={trafficGoalsEnabled}
              onGoalsChange={onFunnelTrafficGoalsChange}
              openSettings={openSettings}
              onJourneyOpen={onTrafficJourneyOpen}
              velocityContext={trafficVelocityContext}
              onKeywordExport={onKeywordExport}
              hiddenCampaignKeys={hiddenCampaignKeys}
              onToggleHiddenCampaign={onToggleHiddenCampaign}
            />
          </Collapse>
        </Box>
      </Stack>
    </Tabs.Panel>
  );
}
